/*
<summary>
   This functional component is all about rendering the Crimp Tool Inspection entry screen —
   AWG-based crimp height/tensile strength measurement entry (gated by a Start/Pause/Complete
   session timer.
   Developer: Aashish Singh, Created Date: 31-Aug-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import toast from 'react-hot-toast';
import { useHistory, useParams } from 'react-router-dom';
import authStore from '../../../core/stores/auth-store';
import { useStore } from '../../../contexts/store-provider';
import { getTenantIdFromLocalStorage } from '../../../helpers/localstorage-helper';
import InspectionStatusEnum from '../../../constants/enums/inspection-status-enum';
import UsageStatusEnum from '../../../constants/enums/usage-status-enum';
import {
  IAddCrimpToolInspectionRequest,
  ICrimpInspectionTemplateValues,
} from '../../../models/forms/IAddCrimpToolInspection';
import { ICrimpInspectionEntryForm } from '../../../models/forms/IEquipmentInspectionSession';
import { IToolManagementVM } from '../../../models/response/IToolManagementResponse';
import RoutesConstants from '../../../shared-components/routes/routes-constants';
import FormLoader from '../../../shared-components/FormLoader/FormLoader';
import worker_script from '../../../shared-components/web-worker/webWorker';
import { formatMessage } from '../../../translations/format-message';
import noMessageIcon from '../../../assets/Images/filterNodata.png';
import './user-created-inspection.css';
import './crimp-tool-inspection.css';

const timerWorker = new Worker(worker_script);

const initialFormState: ICrimpInspectionEntryForm = {
  AWG: '',
  CrimpHeight: '',
  TensileStrength: '',
  Comments: '',
};

/**
* This function safely parses a crimp inspection record's TemplateValues JSON (the measured
* CH/Tensile values and their individual OK/NG breakdown, which have no dedicated columns of
* their own on the backend) back into a usable object, tolerating blank/malformed values.
*/
const parseTemplateValues = (raw: string): ICrimpInspectionTemplateValues | null => {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ICrimpInspectionTemplateValues;
  } catch {
    return null;
  }
};

/**
* This functional component renders the Crimp Tool Inspection entry screen for a single
* tool row — AWG selection, spec lookup, measured value entry with a live auto-computed
* OK/NG judgment, and the tool's crimp inspection records from the last 24 hours.
*/
const CrimpToolInspection = () => {
  const history = useHistory();
  const { toolId } = useParams<{ toolId: string }>();
  const { toolManagementStore, crimpingInspectionRecordStore, factoryStore } = useStore();
  const { toolManagementDetails, toolManagementState, GetToolManagementByIdService } = toolManagementStore;
  const { allAvailableFactories, GetAllFactoriesService } = factoryStore;
  const {
    toolCrimpInspectionLast24Hours,
    getCrimpToolInspectionLast24HoursState,
    GetCrimpToolInspectionLast24HoursService,
    AddCrimpToolInspectionService,
    addCrimpToolInspectionState,
  } = crimpingInspectionRecordStore;

  const [row, setRow] = useState<IToolManagementVM | undefined>(undefined);
  const [form, setForm] = useState<ICrimpInspectionEntryForm>(initialFormState);
  const [webWorkerTime, setWebWorkerTime] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [inspectionId, setInspectionId] = useState<number | null>(null);
  const [sessionStartDate, setSessionStartDate] = useState<string>('');
  // Captured once, when this tool's inspection page is entered (fresh link or "Perform
  // Action" from Tool Management) - not a live clock, so it stays fixed for the page visit.
  const [pageEnteredAt, setPageEnteredAt] = useState<Date>(() => new Date());
  // Refs so the history.block callback below (registered once) always reads the latest
  // isTimerRunning value / handlePauseTimer closure instead of a stale one from mount.
  const isTimerRunningRef = useRef(isTimerRunning);
  const handlePauseTimerRef = useRef<() => Promise<boolean | undefined>>();
  const currentToolIdRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    GetAllFactoriesService(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setRow(undefined);
    setForm(initialFormState);
    setInspectionId(null);
    setSessionStartDate('');
    setIsTimerRunning(false);
    setWebWorkerTime(0);
    setPageEnteredAt(new Date());
    timerWorker.postMessage({ turn: 'reset' });
    if (toolId) {
      GetToolManagementByIdService(Number(toolId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolId]);

  useEffect(() => {
    currentToolIdRef.current = toolId ? Number(toolId) : undefined;
  }, [toolId]);

  useEffect(() => {
    if (toolManagementDetails && toolManagementDetails.ToolId === Number(toolId)) {
      setRow(toolManagementDetails);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolManagementDetails]);

  useEffect(() => {
    // The right-side "last 24 hours" table always needs this regardless of inspection
    // status - only the resume-old-session hydration below is conditional on it.
    if (toolId) {
      GetCrimpToolInspectionLast24HoursService(Number(toolId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolId]);

  useEffect(() => {
    // Inspected -> this is a fresh inspection, start blank. NotInspected -> a prior session
    // may exist (paused, State 1/2) or may not (never started) - resume it if found.
    if (!row || row.InspectionStatus !== InspectionStatusEnum.NotInspected) return;
    const inProgress = toolCrimpInspectionLast24Hours.find(
      (record) => record.State !== 3 && record.ToolId === row.ToolId
    );
    if (!inProgress) return;

    setInspectionId(inProgress.Id);
    setSessionStartDate(inProgress.StartDate);
    setWebWorkerTime(inProgress.Duration || 0);
    const values = parseTemplateValues(inProgress.TemplateValues);
    setForm({
      AWG: inProgress.Awg || '',
      CrimpHeight:
        values?.CrimpHeightInput ?? (values ? String(values.MeasuredCH) : ''),
      TensileStrength:
        values?.TensileStrengthInput ?? (values ? String(values.MeasuredTensile) : ''),
      Comments: values?.Comments ?? (inProgress.NgReason || ''),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row, toolCrimpInspectionLast24Hours]);

  useEffect(() => {
    timerWorker.onmessage = ({ data: { time } }: any) => {
      setWebWorkerTime(time);
    };
    return () => {
      timerWorker.postMessage({ turn: 'off' });
    };
  }, []);

  const ownerFactoryName =
    allAvailableFactories.find((option) => option.id === Number(row?.OwnerFactoryId))?.value || '-';

  const formatTime = (seconds: number) => {
    const totalSeconds = Math.floor(seconds);
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    return [hrs, mins, secs].map((v) => String(v).padStart(2, '0')).join(':');
  };

  const handleFieldChange = (field: keyof ICrimpInspectionEntryForm, value: string) => {
    setForm({ ...form, [field]: value });
  };

  const handleAwgChange = (awg: string) => {
    setForm({ ...form, AWG: awg, CrimpHeight: '', TensileStrength: '' });
  };

  // Measured values are plain numbers - "e"/"E" (exponent) and "+" are valid number-input
  // characters, so they're blocked as typed and stripped from pasted text.
  const blockExponentKeys = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (['e', 'E', '+'].includes(e.key)) e.preventDefault();
  };
  const handleMeasuredChange = (
    field: 'CrimpHeight' | 'TensileStrength',
    value: string
  ) => {
    if (/[eE+]/.test(value)) return;
    handleFieldChange(field, value);
  };

  const selectedCrimpRow = row?.CrimpRows?.find((cr) => cr.AWG === form.AWG);

  const chOk =
    !!selectedCrimpRow &&
    form.CrimpHeight !== '' &&
    selectedCrimpRow.LowerLimitCH != null &&
    selectedCrimpRow.UpperLimitCH != null &&
    Number(form.CrimpHeight) >= selectedCrimpRow.LowerLimitCH &&
    Number(form.CrimpHeight) <= selectedCrimpRow.UpperLimitCH;
  const tensileOk =
    !!selectedCrimpRow &&
    form.TensileStrength !== '' &&
    selectedCrimpRow.TensileStrengthLimit != null &&
    Number(form.TensileStrength) >= selectedCrimpRow.TensileStrengthLimit;
  const hasMeasurements = !!selectedCrimpRow && form.CrimpHeight !== '' && form.TensileStrength !== '';
  const judgment: 'OK' | 'NG' | null = hasMeasurements ? (chOk && tensileOk ? 'OK' : 'NG') : null;

  const buildPayload = (state: number, endDate?: string): IAddCrimpToolInspectionRequest => {
    const templateValues: ICrimpInspectionTemplateValues = {
      MeasuredCH: Number(form.CrimpHeight) || 0,
      MeasuredTensile: Number(form.TensileStrength) || 0,
      ChOk: chOk,
      TensileOk: tensileOk,
      CrimpHeightInput: form.CrimpHeight,
      TensileStrengthInput: form.TensileStrength,
      Comments: form.Comments,
    };
    return {
      Id: inspectionId,
      ToolId: row!.ToolId,
      ToolNumber: row!.ToolManagementNo,
      StartDate: sessionStartDate,
      EndDate: endDate || null,
      Duration: webWorkerTime,
      State: state,
      Awg: form.AWG,
      UpperLimit: Number(selectedCrimpRow?.UpperLimitCH ?? 0),
      LowerLimit: Number(selectedCrimpRow?.LowerLimitCH ?? 0),
      TensileStrength: Number(selectedCrimpRow?.TensileStrengthLimit ?? 0),
      TemplateValues: JSON.stringify(templateValues),
      CalculatedResult: judgment ? (judgment === 'OK' ? 1 : 0) : null,
      NgReason: judgment === 'NG' ? form.Comments || null : null,
      CreatedBy: authStore.getUserId,
      UpdatedBy: authStore.getUserId,
      TenantId: Number(getTenantIdFromLocalStorage()),
    };
  };

  const handleStartTimer = async () => {
    if (!row) return;

    // Bug fix: a tool currently InUse (on a tray) cannot be pulled aside for inspection.
    if (row.UsageStatus === UsageStatusEnum.InUse) {
      toast.error(formatMessage('ToolInUse'));
      return;
    }

    if (inspectionId) {
      timerWorker.postMessage({ turn: 'on', initialTime: webWorkerTime });
      setIsTimerRunning(true);
      return;
    }

    const startedForToolId = row.ToolId;

    await GetCrimpToolInspectionLast24HoursService(startedForToolId);
    if (currentToolIdRef.current !== startedForToolId) return;
    const existingSession = crimpingInspectionRecordStore.toolCrimpInspectionLast24Hours.find(
      (record) => record.State !== 3 && record.ToolId === startedForToolId
    );
    if (existingSession) {
      setInspectionId(existingSession.Id);
      setSessionStartDate(existingSession.StartDate);
      setWebWorkerTime(existingSession.Duration || 0);
      const values = parseTemplateValues(existingSession.TemplateValues);
      setForm({
        AWG: existingSession.Awg || '',
        CrimpHeight: values?.CrimpHeightInput ?? (values ? String(values.MeasuredCH) : ''),
        TensileStrength: values?.TensileStrengthInput ?? (values ? String(values.MeasuredTensile) : ''),
        Comments: values?.Comments ?? (existingSession.NgReason || ''),
      });
      timerWorker.postMessage({ turn: 'on', initialTime: existingSession.Duration || 0 });
      setIsTimerRunning(true);
      return;
    }

    const now = new Date().toISOString();
    const started = await AddCrimpToolInspectionService({
      ToolId: startedForToolId,
      ToolNumber: row.ToolManagementNo,
      StartDate: now,
      State: 1,
      Duration: 0,
      Awg: '',
      UpperLimit: 0,
      LowerLimit: 0,
      TensileStrength: 0,
      TemplateValues: '',
      CalculatedResult: null,
      NgReason: null,
      CreatedBy: authStore.getUserId,
      TenantId: Number(getTenantIdFromLocalStorage()),
    });
    if (!started || currentToolIdRef.current !== startedForToolId) return;

    setSessionStartDate(now);
    await GetCrimpToolInspectionLast24HoursService(startedForToolId);
    if (currentToolIdRef.current !== startedForToolId) return;
    const freshest = crimpingInspectionRecordStore.toolCrimpInspectionLast24Hours.find(
      (record) => record.ToolId === startedForToolId
    );
    if (freshest) setInspectionId(freshest.Id);
    timerWorker.postMessage({ turn: 'on', initialTime: webWorkerTime });
    setIsTimerRunning(true);
  };

  const handlePauseTimer = async () => {
    timerWorker.postMessage({ turn: 'off' });
    setIsTimerRunning(false);
    isTimerRunningRef.current = false;
    if (!row || !inspectionId) return false;
    const paused = await AddCrimpToolInspectionService(buildPayload(2));
    if (paused) {
      toast.success(formatMessage('inspection_paused_successfully'));
    }
    return paused;
  };

  // Always keep the ref pointed at the latest handlePauseTimer closure (it captures row,
  // inspectionId, sessionStartDate etc., all of which change after mount).
  useEffect(() => {
    handlePauseTimerRef.current = handlePauseTimer;
  });

  useEffect(() => {
    isTimerRunningRef.current = isTimerRunning;
  }, [isTimerRunning]);

  // Intercepts every navigation attempt away from this page (sidebar/module links, browser
  // back/forward, our own history.push calls) while a session is running. On confirming the
  // native "leave page?" prompt, pauses the session first, then completes the navigation the
  // user originally requested - registered once (via refs) so it never fires the pause with
  // stale state.
  useEffect(() => {
    const unblock = history.block((location) => {
      if (!isTimerRunningRef.current) return undefined;
      const confirmed = window.confirm(formatMessage('exit_warning'));
      if (!confirmed) return false;

      isTimerRunningRef.current = false;
      unblock();
      Promise.resolve(handlePauseTimerRef.current?.()).finally(() => {
        history.push(location.pathname + location.search);
      });
      return false;
    });
    return () => unblock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
  * Back button handler - pauses the in-progress session (same as the Pause button) before
  * navigating away if a timer is currently running, so an in-progress inspection isn't lost
  * silently; navigates immediately if no session is active. Marks isTimerRunningRef false
  * first so the history.block listener above doesn't also intercept this already-confirmed
  * navigation.
  */
  const handleBackClick = async () => {
    if (isTimerRunning) {
      await handlePauseTimer();
    }
    isTimerRunningRef.current = false;
    history.push(RoutesConstants.ToolManagement);
  };

  const handleComplete = async () => {
    if (!row || !inspectionId) return;
    timerWorker.postMessage({ turn: 'off' });
    setIsTimerRunning(false);

    const now = new Date().toISOString();
    const completed = await AddCrimpToolInspectionService(buildPayload(3, now));
    if (completed) {
      toast.success(formatMessage('inspection_completed_successfully'));
      isTimerRunningRef.current = false;
      history.push(RoutesConstants.ToolManagement);
    }
  };

  const isCompleteDisabled =
    !inspectionId || !form.AWG || form.CrimpHeight === '' || form.TensileStrength === '' ||
    (judgment === 'NG' && !form.Comments);

  const buttons = isTimerRunning
    ? [
        { labelKey: 'pause', className: 'btn-secondary', onClick: handlePauseTimer, isDisabled: false },
        { labelKey: 'complete', className: 'btn-primary', onClick: handleComplete, isDisabled: isCompleteDisabled },
      ]
    : [
        { labelKey: 'start', className: 'btn-success', onClick: handleStartTimer, isDisabled: !row },
      ];

  const completedRecords = toolCrimpInspectionLast24Hours.filter((record) => record.State === 3);

  const formatRecordTime = (value: string) => {
    const date = new Date(value);
    if (isNaN(date.getTime())) return '-';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  return (
      <div className="uci-page">
      <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
        <div className="row align-items-center">
          <div className="col">
            <div className="page_title_wrap">
              <div className="page_title_wrap">
                <ul className="add-list operator-back">
                  <li className="process-chart">
                    <Button
                      title={formatMessage('operator_list_detail')}
                      className="btn btn-secondary"
                      onClick={handleBackClick}
                    >
                      <FormattedMessage id="back" />
                    </Button>
                  </li>
                  <li>
                    <h1 className="page_title"><FormattedMessage id="equipment_inspection_crimp_tool" /> </h1>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {(toolManagementState.inProgress ||
        getCrimpToolInspectionLast24HoursState.inProgress ||
        addCrimpToolInspectionState.inProgress) && (
        <FormLoader
          loading={
            toolManagementState.inProgress ||
            getCrimpToolInspectionLast24HoursState.inProgress ||
            addCrimpToolInspectionState.inProgress
          }
        />
      )}
      <div className="card card-page uci-card cti-form-card">
        <div className="row">
          <div className="col-xxl-3 col-lg-4 bg-light p-2 border-right">
            <div className="card-body">
              <div className="cti-card-header">
                <FormattedMessage id="crimp_tool_inspection" />
              </div>
              <div className="row g-2 uci-info-row justify-space-between p-0">
                <div className="form-group mb-3 col-6">
                  <label className="uci-field-label">
                    <FormattedMessage id="inspection_date_time" />
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={pageEnteredAt.toLocaleString()}
                    disabled
                    readOnly
                  />
                </div>
                <div className="form-group mb-3 col-6">
                  <label className="uci-field-label">
                    <FormattedMessage id="tool_management_no" />
                  </label>
                  <input
                    type="text"
                    className="form-control fw-bold"
                    value={row?.ToolManagementNo || ''}
                    disabled
                    readOnly
                  />
                </div>
              </div>

              <div className="row g-2 uci-info-row justify-space-between p-0">
                <div
                  className="col-6 mb-3"
                  title={ownerFactoryName}
                >
                  <p className="small text-muted"><FormattedMessage id="owner_factory" /></p>
                  <p className="text-dark uci-info-value">{ownerFactoryName}</p>
                </div>
                <div
                  className="col-6 mb-3"
                  title={row?.ToolManufacturer || ''}
                >
                  <p className="small text-muted"><FormattedMessage id="tool_manufacturer" /></p>
                  <p className="text-dark uci-info-value">{row?.ToolManufacturer || '-'}</p>
                </div>
                <div
                  className="col-12 mb-3"
                  title={`${row?.ReelModel || '-'} / ${row?.LooseTerminalModel || '-'}`}
                >
                  <p className="small text-muted">
                    <FormattedMessage id="reel_model" /> / <FormattedMessage id="loose_terminal_model" />
                  </p>
                  <p className="text-dark uci-info-value">
                    {row?.ReelModel || '-'} / {row?.LooseTerminalModel || '-'}
                  </p>
                </div>
              </div>

              <div className="form-group mb-3 col-12">
                <label className="uci-field-label">
                  <FormattedMessage id="cable_size_awg" />
                  <span className="text-danger"> *</span>
                </label>
                <div className="cti-awg-pills">
                  {(row?.CrimpRows || []).map((cr) => (
                    <button
                      key={cr.AWG}
                      type="button"
                      className={`btn cti-awg-pill ${form.AWG === cr.AWG ? 'active' : ''}`}
                      disabled={!isTimerRunning}
                      onClick={() => handleAwgChange(cr.AWG)}
                    >
                      {cr.AWG}
                    </button>
                  ))}
                </div>
              </div>

              {selectedCrimpRow && (
                <div className="cti-spec-box  mb-3">
                  <div className="cti-spec-title">
                    <FormattedMessage id="specification" /> (AWG {selectedCrimpRow.AWG})
                  </div>
                  <div className="cti-spec-row">
                    <span><FormattedMessage id="ch_mm" /></span>
                    <span className="fw-bold">
                      {selectedCrimpRow.LowerLimitCH} ~ {selectedCrimpRow.UpperLimitCH} mm
                    </span>
                  </div>
                  <div className="cti-spec-row">
                    <span><FormattedMessage id="tensile_strength_n" /></span>
                    <span className="fw-bold">{selectedCrimpRow.TensileStrengthLimit}+ N</span>
                  </div>
                </div>
              )}

              <div className="form-group mb-3 col-12">
                <label className="uci-field-label">
                  <FormattedMessage id="measured_values" />
                  <span className="text-danger"> *</span>
                </label>
                <div className="row g-2">
                  <div className="col-6">
                    <input
                      type="number"
                      className="form-control text-center"
                      placeholder="0.00"
                      value={form.CrimpHeight}
                      disabled={!isTimerRunning || !form.AWG}
                      onKeyDown={blockExponentKeys}
                      onChange={(e) => handleMeasuredChange('CrimpHeight', e.target.value)}
                    />
                    <div className="cti-input-caption"><FormattedMessage id="crimp_height" /> (mm)</div>
                  </div>
                  <div className="col-6">
                    <input
                      type="number"
                      className="form-control text-center"
                      placeholder="0"
                      value={form.TensileStrength}
                      disabled={!isTimerRunning || !form.AWG}
                      onKeyDown={blockExponentKeys}
                      onChange={(e) => handleMeasuredChange('TensileStrength', e.target.value)}
                    />
                    <div className="cti-input-caption"><FormattedMessage id="tensile_strength_n" /></div>
                  </div>
                </div>

                <div className="form-group mb-3 mt-2">
                  <label className="uci-field-label">
                    <FormattedMessage id="judgment" />
                  </label>
                  <div className="cti-ok-ng-toggle">
                    <div className={`cti-ok-btn ${judgment === 'OK' ? 'active' : ''}`}>
                      <FormattedMessage id="ok" />
                    </div>
                    <div className={`cti-ng-btn ${judgment === 'NG' ? 'active' : ''}`}>
                      <FormattedMessage id="ng" />
                    </div>
                  </div>
                </div>

                {judgment === 'NG' && (
                  <div className="form-group mb-3">
                    <label className="uci-field-label">
                      <FormattedMessage id="comments" />
                      <span className="text-danger"> *</span>
                    </label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={form.Comments}
                      disabled={!isTimerRunning || !form.AWG}
                      onChange={(e) => handleFieldChange('Comments', e.target.value)}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="col-xxl-9 col-lg-8 p-2">
            <div className="">
              <div className="cti-card-header">
                <FormattedMessage id="last_24_hours_records" /> ({row?.ToolManagementNo || '-'})
              </div>
              {completedRecords.length > 0 ? (
                <div className="table-responsive">
                  <table className="table cti-records-table">
                    <thead>
                      <tr>
                        <th><FormattedMessage id="inspection_date_time" /></th>
                        <th><FormattedMessage id="awg" /></th>
                        <th><FormattedMessage id="ch_judgment" /></th>
                        <th><FormattedMessage id="tensile_judgment" /></th>
                        <th><FormattedMessage id="inspector" /></th>
                      </tr>
                    </thead>
                    <tbody>
                      {completedRecords.map((record) => {
                        const values = parseTemplateValues(record.TemplateValues);
                        const inspectorName =
                          record.CreatedBy === authStore.getUserId
                            ? authStore.getUserName
                            : record.CreatedBy;
                        return (
                          <tr key={record.Id}>
                            <td>{formatRecordTime(record.StartDate)}</td>
                            <td>{record.Awg}</td>
                            <td>
                              <span
                                className={`cti-badge ${values?.ChOk ? 'cti-badge-ok' : 'cti-badge-ng'}`}
                              >
                                {values?.ChOk ? <FormattedMessage id="ok" /> : <FormattedMessage id="ng" />}
                              </span>
                            </td>
                            <td>
                              <span
                                className={`cti-badge ${values?.TensileOk ? 'cti-badge-ok' : 'cti-badge-ng'}`}
                              >
                                {values?.TensileOk ? <FormattedMessage id="ok" /> : <FormattedMessage id="ng" />}
                              </span>
                            </td>
                            <td>{inspectorName}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="message">
                  <div className="d-flex message-area">
                    <div className="no-data-img">
                      <img
                        src={noMessageIcon}
                        alt=""
                        className="no_message"
                      />
                    </div>
                    <div>
                      <h3 className="fw-bold display-6">
                        <FormattedMessage id="no_record" />
                      </h3>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="uci-timer-bar">
        <div className="row align-items-center g-0 w-100">
          <div className="col-auto">
            <div className="uci-timer-group d-flex">
              <label className="badge bg-primary fw-medium">
                <FormattedMessage id="duration" />
              </label>
              <div className="d-flex align-items-center">
                <div className="uci-timer-value">
                  <p>{formatTime(webWorkerTime)}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="col">
            <div className="d-flex gap-1 gap-xxl-1 flex-wrap justify-content-end">
              {buttons.map((btn) => (
                <div
                  className={`uci-btn-wrap ${btn.isDisabled ? 'cursor-not-allowed' : ''}`}
                  key={btn.labelKey}
                >
                  <button
                    type="button"
                    className={`btn ${btn.className} ${btn.isDisabled ? 'disabled' : ''}`}
                    disabled={btn.isDisabled}
                    onClick={btn.onClick}
                  >
                    <FormattedMessage id={btn.labelKey} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      </div>
  );
};

export default observer(CrimpToolInspection);
